<template>
  <div class="rankings-page">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- 页面标题 -->
      <div class="text-center mb-12">
        <h1 class="text-3xl font-bold mb-3">工具排行榜</h1>
        <p class="text-neutral-500 max-w-2xl mx-auto">
          发现最受欢迎、评分最高的AI工具
        </p>
      </div>

      <!-- 排行榜类型标签 -->
      <div
        class="bg-neutral-100 rounded-xl p-1 mb-8 inline-block w-full sm:w-auto"
      >
        <div class="flex flex-wrap gap-2">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="selectTab(tab.id)"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 sm:flex-none justify-center flex items-center',
              currentTab === tab.id
                ? 'bg-primary text-white shadow-sm'
                : 'bg-transparent text-neutral-600 hover:bg-white/50',
            ]"
          >
            <i :class="['fa mr-2', tab.icon]"></i>{{ tab.name }}
          </button>
        </div>
      </div>

      <!-- 筛选栏 -->
      <div
        class="flex flex-col md:flex-row justify-between items-center mb-8 gap-4"
      >
        <div class="text-lg font-bold flex items-center">
          <span>{{ currentTabName }}</span>
          <span class="text-neutral-500 font-normal ml-2">
            <select
              v-model="timeRange"
              class="bg-transparent border-none text-sm focus:outline-none cursor-pointer hover:text-primary"
              @change="getRankList()"
            >
              <option value="all">全部时间</option>
              <option value="week">本周</option>
              <option value="month">本月</option>
              <option value="year">本年</option>
            </select>
          </span>
        </div>
        <div class="w-full md:w-auto">
          <select
            v-model="selectedCategory"
            class="w-full md:w-48 px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            @change="getRankList()"
          >
            <option value="all">所有分类</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">
              {{ cat.title }}
            </option>
          </select>
        </div>
      </div>

      <!-- 排行榜列表 -->
      <div class="space-y-4">
        <div
          v-for="(tool, index) in tools"
          :key="tool.id"
          class="bg-white rounded-xl border border-neutral-200 p-4 flex items-center hover-lift transition-all duration-300 cursor-pointer group"
          @click="openToolDetail(tool)"
        >
          <!-- 排名序号 -->
          <div
            class="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm sm:text-lg mr-3 sm:mr-4 transition-transform group-hover:scale-110"
            :class="getRankClass(index)"
          >
            {{ index + 1 }}
          </div>

          <!-- 缩略图 -->
          <img
            :src="tool.cover"
            :alt="tool.title"
            class="w-16 h-12 sm:w-20 sm:h-14 object-cover rounded-lg mr-4 bg-neutral-100 shadow-sm"
          />

          <!-- 信息 -->
          <div class="flex-grow min-w-0 mr-4">
            <h3
              class="font-bold text-base sm:text-lg truncate group-hover:text-primary transition-colors"
            >
              {{ tool.title }}
            </h3>
            <div class="flex items-center text-xs sm:text-sm text-neutral-500">
              <span class="mr-3 px-2 py-0.5 bg-neutral-100 rounded-md">{{
                getCategoryName(tool.categoryid)
              }}</span>

              <span
                v-if="getSubName(tool.subid)"
                class="mr-3 px-2 py-0.5 bg-neutral-100 rounded-md"
                >{{ getSubName(tool.subid) }}</span
              >
              <span class="flex items-center text-yellow-400">
                <i class="fa fa-star mr-1"></i> {{ tool.rate }}
              </span>
            </div>
          </div>

          <!-- 数值展示 -->
          <div class="text-right flex-shrink-0">
            <div class="text-base sm:text-lg font-bold text-primary">
              {{ getMetricValue(tool) }}
            </div>
            <div class="text-xs text-neutral-400">{{ getMetricLabel() }}</div>
          </div>

          <!-- 箭头 -->
          <div
            class="ml-4 hidden sm:block text-neutral-300 group-hover:text-primary transition-colors"
          >
            <i class="fa fa-chevron-right"></i>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="tools.length === 0" class="text-center py-12">
          <p class="text-neutral-500">暂无数据</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { globalState } from "@/state";

export default {
  name: "RankingsPage",
  data() {
    return {
      categories: [],
      tools: [],
      currentTab: "download",
      timeRange: "month",
      selectedCategory: "all",
      subList: [],
      tabs: [
        { id: "download", name: "下载榜", icon: "fa-download" },
        { id: "rating", name: "评分榜", icon: "fa-star" },
        { id: "new_download", name: "新品榜", icon: "fa-rocket" },
        // { id: "trending", name: "飙升榜", icon: "fa-line-chart" },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
    currentTabName() {
      const tab = this.tabs.find((t) => t.id === this.currentTab);
      return tab ? tab.name : "";
    },
  },
  created() {
    this.getCategory();
  },
  methods: {
    getCategory() {
      this.$axios({
        url: "subinfo/list",
      }).then((res) => {
        this.subList = res.data;
      });
      this.$axios({
        url: "category/list",
      }).then(async (res) => {
        this.categories = res.data;
        await this.getRankList();
      });
    },
    getSubName(id) {
      var sub = this.subList.find((c) => c.id === id);
      return sub ? sub.title : "";
    },
    async getRankList() {
      var uid = "";
      if (this.isLogin) {
        uid = this.user.uid;
      }
      var data = await this.$aesEncrypt({
        timeType: this.timeRange,
        category: this.selectedCategory,
      });
      this.$axios({
        url: "aitool/rank/month/" + this.currentTab + "?uid=" + uid,
        method: "POST",
        data: {
          data: data,
        },
      }).then((res) => {
        if (res.code == 200) {
          this.tools = res.data;
        }
      });
    },
    selectTab(id) {
      this.currentTab = id;
      this.getRankList();
    },
    openToolDetail(tool) {
      globalState.openModal(tool.id, this.categories);
    },
    getCategoryName(id) {
      const category = this.categories.find((c) => c.id === id);
      return category ? category.title : "未知";
    },
    getRankClass(index) {
      if (index === 0) return "bg-yellow-100 text-yellow-600";
      if (index === 1) return "bg-gray-100 text-gray-600";
      if (index === 2) return "bg-orange-100 text-orange-600";
      return "bg-neutral-50 text-neutral-500";
    },
    getMetricValue(tool) {
      switch (this.currentTab) {
        case "download":
          return this.formatNumber(tool.monthDownloadCount);
        case "rating":
          return tool.rate;
        case "new_download":
          return this.formatDate(tool.createTime);
        case "trending":
          return "+" + (tool.rating * 10).toFixed(0) + "%";
        default:
          return "";
      }
    },
    getMetricLabel() {
      switch (this.currentTab) {
        case "download":
          return "次下载";
        case "rating":
          return "评分";
        case "new_download":
          return "发布日期";
        case "trending":
          return "热度指数";
        default:
          return "";
      }
    },
    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "k";
      }
      return num;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("zh-CN", {
        month: "numeric",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
